import type { DefaultDtoType, Field } from "../Field";
import { initField } from "../Field";
import type { InferOutput } from "valibot";
import {
  array,
  boolean,
  fallback,
  intersect,
  literal,
  maxLength,
  minLength,
  nullable,
  nullish,
  number,
  object,
  pipe,
  variant,
} from "valibot";
import {
  currency,
  dataName,
  decimalValue,
  errorTranslation,
  integerValue,
  label,
  operator,
} from "../../utils/validation/base";
import { useTranslation } from "../../state/i18n";
import { TextInput } from "../../components/inputs/TextInput";
import {
  MAX_CALCULATION_TERMS,
  MAX_DECIMAL_PRECISION,
  MAX_NUMBER_XL,
  MAX_NUMBER_XL_NEGATIVE,
  NUMBER_CONDITION_KEYS,
} from "../../constants";
import { useFormServices } from "../../services/formServices";
import { NumberInput } from "../../components/inputs/NumberInput";
import { Toggle } from "../../components/toggle/Toggle";
import { createMemo, Show } from "solid-js";
import type { FieldType } from "../../types";
import { createOptionsForCalculationTerms, createOptionsForCurrencies } from "../../utils/selectOptionsUtil";
import { valueReferenceOfTypes } from "../../utils/validation/references";
import type { VariableType } from "../../variables/types";
import { defaultNumberFilter } from "../../pages/customer/form/filter";
import FormatNumber from "../../components/formatting/FormatNumber";
import { MultiSelectWithSearchInput } from "../../components/menu/MultiSelectWithSearch";
import { SingleSelectInput } from "../../components/menu/Select";
import { InputPreview, PreviewContentLabel } from "../FieldPreviewComponents";
import PropertySection from "../PropertySection";
import { keyFromSelector } from "i18next";
import FieldValueReference from "../FieldValueReference.ts";

const allowedTermFieldTypes: FieldType[] = [
  "barcode",
  "calculation",
  "catalogue",
  "lookup",
  "number",
  "price",
  "radio",
  "rating",
  "readOnlyText",
  "search",
  "slider",
  "smiley",
  "text",
  "textArea",
  "timeDifference",
];

const allowedTermVariableTypes: VariableType[] = ["number"];

const Schema = intersect([
  object({
    label_text: label(),
    data_name: dataName(),
    operator: operator(errorTranslation(keyFromSelector(($) => $.VALIDATION_OPERATOR))),
    terms: pipe(
      array(valueReferenceOfTypes(allowedTermFieldTypes, allowedTermVariableTypes)),
      minLength(1, errorTranslation(keyFromSelector(($) => $.VALIDATION_TERMS_MIN))),
      maxLength(MAX_CALCULATION_TERMS, errorTranslation(keyFromSelector(($) => $.VALIDATION_TERMS_MAX))),
    ),
    initial_value: nullable(decimalValue(MAX_NUMBER_XL_NEGATIVE, MAX_NUMBER_XL)),
    precision: nullable(integerValue(0, MAX_DECIMAL_PRECISION)),
    no_value_on_missing_term: fallback(boolean(), true),
    skip_hidden_terms: fallback(boolean(), false),
  }),
  variant("isPrice", [
    object({
      isPrice: literal(false),
      currency: nullish(currency(errorTranslation(keyFromSelector(($) => $.VALIDATION_CURRENCY)))),
    }),
    object({
      isPrice: literal(true),
      currency: currency(errorTranslation(keyFromSelector(($) => $.VALIDATION_CURRENCY))),
    }),
  ]),
]);
const ValueSchema = number();

export type CalculationFieldProperties = InferOutput<typeof Schema>;
type ValueType = InferOutput<typeof ValueSchema>;

const CalculationField: Field<CalculationFieldProperties, DefaultDtoType, ValueType> = {
  type: "calculation",
  category: "number",
  widget: "com.moreapps.plugins:calculation:13",
  schema: Schema,
  valueSchema: ValueSchema,
  hasData: true,
  defaultProperties: () => ({
    operator: "addition",
    terms: [],
    initial_value: null,
    precision: 2,
    no_value_on_missing_term: true,
    skip_hidden_terms: false,
    isPrice: false,
    currency: undefined,
  }),
  duplicateProperties: (properties, updateReference) => ({
    ...properties,
    terms: properties.terms.map((property) =>
      property instanceof FieldValueReference
        ? (updateReference({ type: "field", value: property }).value as FieldValueReference)
        : property,
    ),
  }),
  ruleBehaviour: {
    conditions: { keys: NUMBER_CONDITION_KEYS, variant: "text" },
    actions: { variant: "text" },
  },
  filter: {
    type: "number",
    definition: defaultNumberFilter,
  },
  stringValue: (value) => value.toString(),
  renderValue: (value) => <FormatNumber value={value} />,
  icon: "calculator",
  titleKey: keyFromSelector(($) => $.CALCULATION_FIELD_LABEL),
  descriptionKey: keyFromSelector(($) => $.CALCULATION_FIELD_DESCRIPTION),
  propertyKeys: {
    data_name: keyFromSelector(($) => $.FIELD_PROPERTY_DATA_NAME),
    label_text: keyFromSelector(($) => $.FIELD_PROPERTY_LABEL_TEXT),
    terms: keyFromSelector(($) => $.FIELD_PROPERTY_CALCULATION_TERMS),
    initial_value: keyFromSelector(($) => $.FIELD_PROPERTY_INITIAL_VALUE),
    no_value_on_missing_term: keyFromSelector(($) => $.FIELD_PROPERTY_NO_VALUE_INVALID_TERM),
    skip_hidden_terms: keyFromSelector(($) => $.FIELD_PROPERTY_SKIP_HIDDEN_TERMS),
    operator: keyFromSelector(($) => $.FIELD_PROPERTY_OPERATOR),
    precision: keyFromSelector(($) => $.FIELD_PROPERTY_PRECISION),
    isPrice: keyFromSelector(($) => $.FIELD_PROPERTY_IS_PRICE),
    currency: keyFromSelector(($) => $.FIELD_PROPERTY_CURRENCY),
  },
  PreviewContent: (props) => {
    const { t } = useTranslation();

    return (
      <PreviewContentLabel uid={props.uid} label={props.properties.label_text}>
        <InputPreview placeholder={t(($) => $.CALCULATION_FIELD_PREVIEW_PLACEHOLDER)} disabled />
      </PreviewContentLabel>
    );
  },
  EditItem: (props) => {
    const { t } = useTranslation();
    const { fieldService, currentScope } = useFormServices();
    const currencyOptions = createMemo(() => createOptionsForCurrencies(t));

    return (
      <>
        <PropertySection>
          <TextInput
            label={t(props.propertyKeys.label_text)}
            value={props.data.properties.label_text}
            onBlur={(e) => {
              fieldService.setLabelProperty(props.uid, e.target.value);
            }}
            errorMessage={props.getError("label_text")}
            required
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_CALCULATION)}>
          <MultiSelectWithSearchInput
            label={t(props.propertyKeys.terms)}
            onSelect={(fieldIds) => props.setValue("terms", fieldIds)}
            options={createOptionsForCalculationTerms(
              props.uid,
              currentScope,
              allowedTermFieldTypes,
              allowedTermVariableTypes,
              t,
            )}
            value={props.data.properties.terms}
            required
            errorMessage={props.getError("terms")}
            width="matchTrigger"
          />
          <NumberInput
            label={t(props.propertyKeys.initial_value)}
            value={props.data.properties.initial_value}
            minValue={MAX_NUMBER_XL_NEGATIVE}
            maxValue={MAX_NUMBER_XL}
            allowDecimal
            onBlur={(value) => props.setValue("initial_value", value)}
            errorMessage={props.getError("initial_value")}
          />
          <SingleSelectInput
            label={t(props.propertyKeys.operator)}
            value={props.data.properties.operator}
            errorMessage={props.getError("operator")}
            required
            placeholder={t(($) => $.FIELD_PROPERTY_OPERATOR_PLACEHOLDER)}
            options={[
              { variant: "text", label: t(($) => $.FIELD_PROPERTY_OPERATOR_ADDITION), value: "addition" },
              { variant: "text", label: t(($) => $.FIELD_PROPERTY_OPERATOR_SUBTRACT), value: "subtract" },
              { variant: "text", label: t(($) => $.FIELD_PROPERTY_OPERATOR_MULTIPLY), value: "multiply" },
              { variant: "text", label: t(($) => $.FIELD_PROPERTY_OPERATOR_DIVIDE), value: "divide" },
              { variant: "text", label: t(($) => $.FIELD_PROPERTY_OPERATOR_AVERAGE), value: "average" },
              { variant: "text", label: t(($) => $.FIELD_PROPERTY_OPERATOR_MINIMUM), value: "minimum" },
              { variant: "text", label: t(($) => $.FIELD_PROPERTY_OPERATOR_MAXIMUM), value: "maximum" },
            ]}
            onSelect={(value) => props.setValue("operator", value)}
            sameWidth
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_OUTPUT)}>
          <Toggle
            block={true}
            label={t(props.propertyKeys.isPrice)}
            checked={props.data.properties.isPrice}
            setChecked={(checked) => props.setValue("isPrice", checked)}
            description={t(($) => $.FIELD_PROPERTY_IS_PRICE_DESCRIPTION)}
          />
          <Show when={props.data.properties.isPrice}>
            <SingleSelectInput
              label={t(props.propertyKeys.currency)}
              value={props.data.properties.currency}
              errorMessage={props.getError("currency")}
              options={currencyOptions()}
              onSelect={(value) => props.setValue("currency", value)}
              sameWidth
            />
          </Show>
          <NumberInput
            label={t(props.propertyKeys.precision)}
            value={props.data.properties.precision}
            minValue={0}
            maxValue={MAX_DECIMAL_PRECISION}
            onBlur={(value) => props.setValue("precision", value)}
            errorMessage={props.getError("precision")}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_BEHAVIOUR)}>
          <Toggle
            block
            label={t(props.propertyKeys.skip_hidden_terms)}
            checked={props.data.properties.skip_hidden_terms}
            setChecked={(value) => {
              props.setValue("skip_hidden_terms", value);
            }}
            description={t(($) => $.FIELD_PROPERTY_SKIP_HIDDEN_TERMS_DESCRIPTION)}
          />
          <Toggle
            block
            label={t(props.propertyKeys.no_value_on_missing_term)}
            checked={props.data.properties.no_value_on_missing_term}
            setChecked={(value) => {
              props.setValue("no_value_on_missing_term", value);
            }}
            description={t(($) => $.FIELD_PROPERTY_NO_VALUE_INVALID_TERM_DESCRIPTION)}
          />
        </PropertySection>

        <PropertySection title={t(($) => $.FIELD_PROPERTIES_SECTION_ADVANCED)}>
          <TextInput
            label={t(props.propertyKeys.data_name)}
            value={props.data.properties.data_name}
            onBlur={(e) => props.setValue("data_name", e.target.value)}
            required
            errorMessage={props.getError("data_name")}
            info={{
              title: t(props.propertyKeys.data_name),
              alt: t(($) => $.FIELD_PROPERTY_DESCRIPTION_ARIA_LABEL, { property: props.propertyKeys.data_name }),
              description: t(($) => $.FIELD_PROPERTY_DATA_NAME_DESCRIPTION),
            }}
          />
        </PropertySection>
      </>
    );
  },
};

export default initField(CalculationField);
